import React, { useState, useEffect, useMemo } from 'react';
import { formatRupiahFull, formatRupiah } from '../data/financialData';
import { useData } from '../DataContext';

interface GLLookupPageProps {
  initialArea: string;
  initialBranch: string;
  activeEntity?: string;
}

export const GLLookupPage: React.FC<GLLookupPageProps> = ({ initialArea, initialBranch, activeEntity }) => {
  const { MONTHS, branches } = useData();
  // Data State
  const [glRows, setGlRows] = useState<any[][]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [uniqueAccounts, setUniqueAccounts] = useState<Array<{ code: string; name: string }>>([]);

  // Filter States
  const [filterAccount, setFilterAccount] = useState<string>('');
  const [filterMonth, setFilterMonth] = useState<string>('');
  
  // Combine area and branch into one clean select state
  const [filterAreaOrBranch, setFilterAreaOrBranch] = useState<string>(
    initialBranch !== 'ALL' ? initialBranch : (initialArea !== 'ALL' ? initialArea : '')
  );
  
  const [filterMinAmount, setFilterMinAmount] = useState<string>('');
  const [filterMaxAmount, setFilterMaxAmount] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  // Fetch unique accounts for autocomplete suggestions on mount
  useEffect(() => {
    const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
    const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';
    
    fetch(`${API_BASE}/api/gl-transactions?entity=${activeEntity ?? 'JUARA_GADAI'}&distinct=accounts`, {
      headers: { 'Authorization': `Bearer ${API_KEY}` }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setUniqueAccounts(data);
        }
      })
      .catch((err) => console.error("Gagal memuat distinct accounts:", err));
  }, [activeEntity]);

  // Sync initial filters if parent props change
  useEffect(() => {
    if (initialBranch !== 'ALL') {
      setFilterAreaOrBranch(initialBranch);
    } else if (initialArea !== 'ALL') {
      setFilterAreaOrBranch(initialArea);
    } else {
      setFilterAreaOrBranch('');
    }
    setCurrentPage(1);
  }, [initialArea, initialBranch]);

  // Determine if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      filterAccount.trim() ||
      filterMonth ||
      filterAreaOrBranch ||
      filterMinAmount ||
      filterMaxAmount
    );
  }, [filterAccount, filterMonth, filterAreaOrBranch, filterMinAmount, filterMaxAmount]);

  // Fetch filtered transaction records from D1
  useEffect(() => {
    if (!hasActiveFilters) {
      setGlRows([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
    const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

    const qParams = new URLSearchParams({
      entity: activeEntity ?? 'JUARA_GADAI'
    });

    if (filterAccount.trim()) {
      qParams.set('acc_query', filterAccount.trim());
    }
    if (filterMonth) {
      qParams.set('mo', filterMonth);
    }
    if (filterAreaOrBranch) {
      if (['Depok', 'Bogor', 'Bekasi'].includes(filterAreaOrBranch)) {
        qParams.set('area', filterAreaOrBranch);
      } else {
        qParams.set('branch', filterAreaOrBranch);
      }
    }
    if (filterMinAmount) {
      qParams.set('min_amount', filterMinAmount);
    }
    if (filterMaxAmount) {
      qParams.set('max_amount', filterMaxAmount);
    }

    fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${API_KEY}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((resData: any) => {
        setGlRows(resData.data || []);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Gagal mengambil data Buku Besar dari server.');
        setIsLoading(false);
      });
  }, [activeEntity, hasActiveFilters, filterAccount, filterMonth, filterAreaOrBranch, filterMinAmount, filterMaxAmount]);

  // Suggestions based on active user typing
  const suggestions = useMemo(() => {
    if (!filterAccount.trim()) return [];
    const query = filterAccount.toLowerCase();
    return uniqueAccounts
      .filter(
        (acc) =>
          acc.name.toLowerCase().includes(query) ||
          acc.code.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [uniqueAccounts, filterAccount]);

  // Reset all filters
  const handleResetFilters = () => {
    setFilterAccount('');
    setFilterMonth('');
    setFilterAreaOrBranch('');
    setFilterMinAmount('');
    setFilterMaxAmount('');
    setCurrentPage(1);
  };

  // Perform filtration in a memoized block
  const filteredRows = glRows;



  // Calculate the total sum of filtered transactions in real-time
  const totalAmount = useMemo(() => {
    return filteredRows.reduce((sum, row) => sum + row[8], 0);
  }, [filteredRows]);

  // Reset page number whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterAccount, filterMonth, filterAreaOrBranch, filterMinAmount, filterMaxAmount]);

  // Paginated Rows
  const paginatedRows = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredRows.slice(startIdx, startIdx + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredRows.length / pageSize) || 1;

  // Group branches by Area for option list structure
  const branchesByArea = useMemo(() => {
    const groups: Record<string, typeof branches> = {
      Depok: [],
      Bogor: [],
      Bekasi: [],
    };
    branches.forEach((b) => {
      if (groups[b.area]) {
        groups[b.area].push(b);
      }
    });
    return groups;
  }, [branches]);

  return (
    <div className="data-table-card animate-in" style={{ minHeight: '500px' }}>
      <style dangerouslySetInnerHTML={{__html: `
        .suggestion-item:hover { background-color: var(--sidebar-item-hover) !important; }
      `}} />
      {/* Top Header & Dynamic Sum KPI */}
      <div className="data-table-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="data-table-title" style={{ fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📖</span> GL Lookup
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Pencarian dan peninjauan seluruh catatan transaksi Buku Besar (GL)
          </div>
        </div>

        {/* Sum Card shown in Top Right */}
        <div 
          className="kpi-sum-card"
          style={{
            background: 'var(--bg-input)',
            border: '1px solid var(--border-light)',
            padding: '12px 20px',
            borderRadius: 'var(--radius-md)',
            textAlign: 'right',
            boxShadow: 'var(--shadow-md)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px', marginBottom: '2px' }}>
            Total Nominal Tersaring
          </div>
          <div 
            style={{ 
              fontSize: '18px', 
              fontWeight: '800', 
              color: totalAmount >= 0 ? 'var(--emerald-light)' : 'var(--red-light)',
              fontFamily: 'monospace'
            }}
          >
            {formatRupiahFull(totalAmount)}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {filteredRows.length.toLocaleString('id-ID')} transaksi cocok
          </div>
        </div>
      </div>

      {/* Loading & Error States */}
      {isLoading && (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div className="spinner" style={{ margin: '0 auto 16px', width: '40px', height: '40px', borderRadius: '50%', border: '3px solid var(--border-light)', borderTopColor: 'var(--emerald)', animation: 'spin 1s linear infinite' }} />
          <div>Memuat data Buku Besar (~154k baris)...</div>
          <style dangerouslySetInnerHTML={{__html: `
            @keyframes spin { to { transform: rotate(360deg); } }
          `}} />
        </div>
      )}

      {error && (
        <div style={{ padding: '24px', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--red)', borderRadius: 'var(--radius-md)', color: 'var(--red-light)', margin: '16px 0' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {!isLoading && !error && (
        <>
          {/* Advanced Filtering Control Panel */}
          <div 
            className="gl-filters-container"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              padding: '20px',
              backgroundColor: 'rgba(255,255,255,0.01)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '20px',
              alignItems: 'end'
            }}
          >
            {/* 1. Account Name Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Nama/Kode Akun</label>
              <input
                type="text"
                placeholder="Cari kas, bank, opex..."
                value={filterAccount}
                onChange={(e) => {
                  setFilterAccount(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  // Small timeout to allow mouseDown to fire before hiding suggestions
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  outline: 'none'
                }}
              />

              {/* Autocomplete Suggestions Dropdown List */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: 'var(--shadow-xl)',
                    zIndex: 1000,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}
                >
                  {suggestions.map((acc, idx) => (
                    <div
                      key={idx}
                      onMouseDown={() => {
                        setFilterAccount(acc.name);
                        setShowSuggestions(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid var(--border-color)',
                        transition: 'background var(--transition-fast)',
                      }}
                      className="suggestion-item"
                    >
                      <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>
                        {acc.name}
                      </span>
                      <span style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '11px', 
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        backgroundColor: 'var(--bg-input)',
                        color: 'var(--text-secondary)'
                      }}>
                        {acc.code}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 2. Month Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Bulan Akuntansi</label>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">Semua Bulan</option>
                {MONTHS.map((m) => (
                  <option key={m.month} value={m.month}>
                    {m.label} ({m.month})
                  </option>
                ))}
              </select>
            </div>

            {/* 3. Area / Branch Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Area atau Cabang</label>
              <select
                value={filterAreaOrBranch}
                onChange={(e) => setFilterAreaOrBranch(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">Semua Area & Cabang</option>
                
                {/* Area Groups */}
                <optgroup label="Area Kota">
                  <option value="Depok">Depok (Semua)</option>
                  <option value="Bogor">Bogor (Semua)</option>
                  <option value="Bekasi">Bekasi (Semua)</option>
                </optgroup>

                {/* Specific Branch Groups */}
                {Object.entries(branchesByArea).map(([areaName, branchList]) => (
                  <optgroup key={areaName} label={`Cabang - ${areaName}`}>
                    {branchList.map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.code} — {b.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* 4. Min/Max Amount Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Nominal Minimum (Absolut)</label>
              <input
                type="number"
                placeholder="Min Rp (e.g. 1000000)"
                value={filterMinAmount}
                onChange={(e) => setFilterMinAmount(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Nominal Maksimum (Absolut)</label>
              <input
                type="number"
                placeholder="Max Rp (e.g. 5000000)"
                value={filterMaxAmount}
                onChange={(e) => setFilterMaxAmount(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  outline: 'none'
                }}
              />
            </div>

            {/* Reset Button */}
            <div>
              <button
                onClick={handleResetFilters}
                className="btn btn-secondary"
                style={{ width: '100%', height: '38px', justifyContent: 'center' }}
                disabled={!hasActiveFilters}
              >
                🔄 Reset Filter
              </button>
            </div>
          </div>

          {/* Data Table Section */}
          {!hasActiveFilters ? (
            /* Efficiency Placeholder State */
            <div 
              style={{ 
                padding: '60px 40px', 
                textAlign: 'center', 
                border: '2px dashed var(--border-light)', 
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'rgba(255,255,255,0.01)',
                margin: '20px 0'
              }}
            >
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>💡</span>
              <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
                Tabel Dinonaktifkan Demi Efisiensi Performa
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '500px', margin: '0 auto' }}>
                Buku Besar memiliki total <strong>150,000+ baris transaksi</strong>. Untuk mencegah browser melambat, silakan masukkan kata kunci atau aktifkan setidaknya satu filter di atas untuk menampilkan baris data.
              </p>
            </div>
          ) : filteredRows.length === 0 ? (
            /* Zero Search Result State */
            <div 
              style={{ 
                padding: '60px 40px', 
                textAlign: 'center', 
                border: '2px dashed var(--border-light)', 
                borderRadius: 'var(--radius-lg)',
                margin: '20px 0'
              }}
            >
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📭</span>
              <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
                Data Transaksi Tidak Ditemukan
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                Tidak ada entri Buku Besar yang sesuai dengan kriteria penyaringan Anda. Silakan coba parameter pencarian lain.
              </p>
            </div>
          ) : (
            /* Main Transaction Table */
            <div style={{ overflowX: 'auto', marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Menampilkan <strong>{paginatedRows.length}</strong> dari <strong>{filteredRows.length.toLocaleString('id-ID')}</strong> transaksi ditemukan
                </span>
                
                {/* Page Size Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Baris per halaman:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      borderRadius: '4px',
                      border: '1px solid var(--border-light)',
                      backgroundColor: 'var(--bg-input)',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
              </div>

              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Bulan</th>
                    <th>Nama Account</th>
                    <th>Deskripsi Jurnal</th>
                    <th>Area</th>
                    <th>Cabang</th>
                    <th style={{ textAlign: 'right' }}>Debit</th>
                    <th style={{ textAlign: 'right' }}>Kredit</th>
                    <th style={{ textAlign: 'right' }}>Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row, index) => {
                    const [mo, , acc_name, desc, branch, area, debit, credit, amount] = row;
                    return (
                      <tr key={index}>
                        <td style={{ whiteSpace: 'nowrap' }}>{mo}</td>
                        <td style={{ fontWeight: '600' }}>{acc_name}</td>
                        <td style={{ color: 'var(--text-accent)', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={desc}>
                          {desc}
                        </td>
                        <td>
                          <span style={{ 
                            padding: '2px 8px', 
                            borderRadius: '12px', 
                            fontSize: '11px', 
                            backgroundColor: area === 'Depok' ? 'rgba(59, 130, 246, 0.12)' : area === 'Bogor' ? 'rgba(139, 92, 246, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                            color: area === 'Depok' ? 'var(--blue-light)' : area === 'Bogor' ? 'var(--purple-light)' : 'var(--emerald-light)',
                            fontWeight: '500'
                          }}>
                            {area}
                          </span>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{branch}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {debit > 0 ? formatRupiah(debit) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {credit > 0 ? formatRupiah(credit) : '—'}
                        </td>
                        <td 
                          style={{ 
                            textAlign: 'right', 
                            fontFamily: 'monospace', 
                            fontWeight: '700',
                            color: amount >= 0 ? 'var(--emerald-light)' : 'var(--red-light)'
                          }}
                        >
                          {formatRupiah(amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginTop: '20px', 
                  paddingTop: '16px', 
                  borderTop: '1px solid var(--border-color)' 
                }}
              >
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                >
                  ⏮ First
                </button>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    ◀ Prev
                  </button>
                  
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '0 8px' }}>
                    Halaman <strong>{currentPage}</strong> dari <strong>{totalPages}</strong>
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    Next ▶
                  </button>
                </div>

                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                >
                  Last ⏭
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GLLookupPage;
